import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';
import { usePageHeader } from '../../../context/PageHeaderContext';
import EventModal, { ScheduleEventData, ExistingSlot } from './EventModal';
import { useTimeSettings, DEFAULT_TIME_SETTINGS } from '../../../hooks/useTimeSettings';
import { btnPrimarySx } from '../../../utils/buttonSx';

interface OccurrenceDto {
  eventId: number;
  name: string;
  eventType: string;
  playlistId: number | null;
  fileId?: number | null;
  color: string;
  startDatetime: string;
  endDatetime: string;
  repeatType: string;
}

interface MediaFile { id: number; original_name: string; mime_type: string; }


interface ScheduleEvent extends ScheduleEventData { id: number; }
interface Playlist { id: number; name: string; }

const DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DAYS_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const pad = (n: number) => String(n).padStart(2,'0');
// fmtTime is defined per-component using account timezone; this is a fallback for module-level use
const fmtTimeBrowser = (iso: string) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

// Icons
const IcoBack = () => <svg width={14} height={14} viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoPrev = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>;
const IcoNext = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>;
const IcoPlus = () => <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;

const navBtnSx = { width: 30, height: 30, borderRadius: '7px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-secondary)', transition: 'all .12s', flexShrink: 0, '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)', borderColor: 'var(--glass-border-strong)' } };

const vtBtnSx = (active: boolean) => ({
  px: '12px', py: '5px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  background: active ? 'var(--glass-hover)' : 'transparent', color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
  border: 'none', fontFamily: 'inherit', transition: 'all .12s',
  '&:hover': active ? {} : { background: 'var(--glass-bg)' },
});

function getMonthStart(year: number, month: number) {
  const d = new Date(year, month, 1);
  let dow = d.getDay() - 1; // Mon=0
  if (dow < 0) dow = 6;
  return dow;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getWeekStart(year: number, month: number, weekOffset: number) {
  // Monday of the week containing the 1st of month, offset by weekOffset
  const first = new Date(year, month, 1);
  let dow = first.getDay() - 1; if (dow < 0) dow = 6;
  const monday = new Date(year, month, 1 - dow + weekOffset * 7);
  return monday;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Compare two instants by their calendar date in the given IANA timezone.
function isSameDayInTz(a: Date, b: Date, tz: string): boolean {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(a) === fmt.format(b);
}

// Return current minute-of-day (0–1439) in the given IANA timezone.
function nowMinutesInTz(tz: string): number {
  const n = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: false, timeZone: tz,
  }).formatToParts(n);
  const h = +(parts.find(p => p.type === 'hour')?.value ?? 0);
  const m = +(parts.find(p => p.type === 'minute')?.value ?? 0);
  return h * 60 + m;
}

// Return the UTC instant corresponding to 00:00 on the calendar date of `d` in the given timezone.
function midnightInTz(d: Date, tz: string): Date {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
  }).formatToParts(d);
  const year  = +(parts.find(p => p.type === 'year')?.value  ?? 0);
  const month = +(parts.find(p => p.type === 'month')?.value ?? 1) - 1;
  const day   = +(parts.find(p => p.type === 'day')?.value   ?? 1);
  // Probe: take naive UTC midnight and subtract the zone offset at that instant.
  const probe = new Date(Date.UTC(year, month, day, 12, 0, 0));
  const off = (probe.getTime() - new Date(
    probe.toLocaleString('en-US', { timeZone: tz })
  ).getTime());
  return new Date(Date.UTC(year, month, day, 0, 0, 0) + off);
}

// Return the hour (0–23) of a UTC instant in the given timezone.
function hourInTz(d: Date, tz: string): number {
  return +(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: tz }).format(d));
}

// Return the minute (0–59) of a UTC instant in the given timezone.
function minuteInTz(d: Date, tz: string): number {
  return +(new Intl.DateTimeFormat('en-US', { minute: '2-digit', timeZone: tz }).format(d));
}

const ScheduleEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useSelector((s: RootState) => s.auth.value);
  const { setBreadcrumbs, setSaveConfig } = usePageHeader();
  // Mobile = below md, where the desktop right panel (with its Save button) is hidden
  const isMobile = useMediaQuery('(max-width:899px)');
  const timeSettings = useTimeSettings();
  const tz = timeSettings.timezone || 'UTC';

  // Format a UTC ISO string as HH:MM in the account timezone (same as topbar clock).
  const fmtTime = (iso: string) => {
    const d = new Date(iso);
    return `${pad(hourInTz(d, tz))}:${pad(minuteInTz(d, tz))}`;
  };

  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameRef = useRef('');

  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [weekOffset, setWeekOffset] = useState(0);
  // Month view: index of the week row expanded to show every record of its days
  const [expandedMonthRow, setExpandedMonthRow] = useState<number | null>(null);
  // Day view: id of the overlap cluster expanded into a full list
  const [expandedDayCluster, setExpandedDayCluster] = useState<number | null>(null);
  const [dayDate, setDayDate] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });

  const [occurrences, setOccurrences] = useState<OccurrenceDto[]>([]);
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Partial<ScheduleEventData> | null>(null);
  const [clickedDate, setClickedDate] = useState<Date | null>(null);
  const [lockedDate, setLockedDate] = useState<string | null>(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Current minute-of-day in the account timezone — matches the topbar clock.
  const [nowMinutes, setNowMinutes] = useState(() => nowMinutesInTz('UTC'));
  useEffect(() => {
    setNowMinutes(nowMinutesInTz(tz));
    const timer = setInterval(() => setNowMinutes(nowMinutesInTz(tz)), 60_000);
    return () => clearInterval(timer);
  }, [tz]);

  useEffect(() => { setExpandedMonthRow(null); }, [year, month, view]);

  // Breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { label: t('schedules.allSchedules'), onClick: () => navigate('/schedules') },
      { label: name || '…' },
    ]);
    return () => setBreadcrumbs([]);
  }, [t, name, navigate, setBreadcrumbs]);

  // Mobile: Save moves into the topbar (row with the menu button)
  useEffect(() => {
    if (!isMobile) { setSaveConfig(null); return; }
    // Publishing is allowed for any non-empty schedule, not only right after an
  // edit — otherwise a schedule opened fresh can never be pushed to the screens
    setSaveConfig({
      dirty: hasUnsaved,
      enabled: hasUnsaved || events.length > 0,
      saving: publishing,
      hint: events.length === 0 ? t('schedules.saveNeedsRecord') : '',
      onSave: () => { handlePublish(); },
    });
    return () => setSaveConfig(null);
  }, [isMobile, hasUnsaved, publishing, events.length, setSaveConfig, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load schedule name
  const fetchSchedule = useCallback(async () => {
    try {
      const events = await axios.get<ScheduleEvent[]>(`/schedules/${id}/events`);
      if (events.data.length > 0 || true) {
        // name comes from the list; get it
        const list = await axios.get<{ id: number; name: string }[]>('/schedules');
        const found = list.data.find(s => String(s.id) === id);
        if (found) { setName(found.name); nameRef.current = found.name; }
      }
    } catch {}
  }, [id]);

  // Load playlists
  const fetchPlaylists = useCallback(async () => {
    try {
      const { data } = await axios.get<Playlist[]>('/playlists');
      setPlaylists(data);
    } catch {}
  }, []);

  // Load media files for content type "File"
  const fetchMediaFiles = useCallback(async () => {
    try {
      const { data } = await axios.get<MediaFile[]>('/media/files');
      setMediaFiles(data);
    } catch {}
  }, []);

  // Compute date range and fetch occurrences
  const fetchOccurrences = useCallback(async () => {
    let from: Date, to: Date;
    if (view === 'month') {
      from = new Date(year, month, 1);
      to   = new Date(year, month + 1, 0, 23, 59, 59);
    } else if (view === 'week') {
      const ws = getWeekStart(year, month, weekOffset);
      from = new Date(ws);
      to   = new Date(ws);
      to.setDate(ws.getDate() + 6);
      to.setHours(23, 59, 59);
    } else {
      from = new Date(dayDate); from.setHours(0, 0, 0, 0);
      to   = new Date(dayDate); to.setHours(23, 59, 59, 999);
    }
    try {
      const { data } = await axios.get<OccurrenceDto[]>(`/schedules/${id}/occurrences`, {
        params: { from: from.toISOString(), to: to.toISOString() },
      });
      setOccurrences(data);
    } catch {}
  }, [id, view, year, month, weekOffset, dayDate]);

  // The side panel lists events, not their occurrences: a repeating event is one
  // record with its rule, not one card per day.
  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get<ScheduleEvent[]>(`/schedules/${id}/events`);
      setEvents(data);
    } catch {}
  }, [id]);

  useEffect(() => { if (auth) { fetchSchedule(); fetchPlaylists(); fetchMediaFiles(); } }, [auth, fetchSchedule, fetchPlaylists, fetchMediaFiles]);
  useEffect(() => { if (auth) { fetchOccurrences(); fetchEvents(); } }, [auth, fetchOccurrences, fetchEvents]);

  const handleNameBlur = async () => {
    if (!name || name === nameRef.current) return;
    setSavingName(true);
    try {
      await axios.patch(`/schedules/${id}`, { name });
      nameRef.current = name;
    } catch {}
    setSavingName(false);
  };

  const prevPeriod = () => {
    if (view === 'month') {
      if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1);
    } else if (view === 'week') {
      setWeekOffset(w => w - 1);
    } else {
      setDayDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); setYear(n.getFullYear()); setMonth(n.getMonth()); return n; });
    }
  };
  const nextPeriod = () => {
    if (view === 'month') {
      if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
    } else if (view === 'week') {
      setWeekOffset(w => w + 1);
    } else {
      setDayDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); setYear(n.getFullYear()); setMonth(n.getMonth()); return n; });
    }
  };
  const goToday = () => {
    const n = new Date();
    const y = n.getFullYear(); const mo = n.getMonth();
    setYear(y); setMonth(mo);
    if (view === 'week') {
      const first = new Date(y, mo, 1);
      let dow = first.getDay() - 1; if (dow < 0) dow = 6;
      const week0Start = new Date(y, mo, 1 - dow);
      setWeekOffset(Math.floor((n.getTime() - week0Start.getTime()) / (7 * 86400_000)));
    } else if (view === 'day') {
      const d = new Date(); d.setHours(0,0,0,0); setDayDate(d);
    } else {
      setWeekOffset(0);
    }
  };

  const openNewEvent = (date?: Date) => {
    const start = date ? new Date(date) : new Date();
    start.setMinutes(0, 0, 0);
    if (date) {
      // Derive locked date from the company timezone so it matches the grid column
      const lDate = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(date);
      setLockedDate(lDate);
      setEditingEvent({ repeatType: 'none', eventType: 'content', color: '#6366f1' });
    } else {
      setLockedDate(null);
      const end = new Date(start.getTime() + 3600_000);
      setEditingEvent({ startDatetime: start.toISOString(), endDatetime: end.toISOString(), repeatType: 'none', eventType: 'content', color: '#6366f1' });
    }
    setClickedDate(date ?? null);
    setModalOpen(true);
  };

  const openEditEvent = (eventId: number) => {
    axios.get<ScheduleEvent[]>(`/schedules/${id}/events`).then(({ data }) => {
      const ev = data.find(e => e.id === eventId);
      if (ev) { setLockedDate(null); setEditingEvent(ev); setModalOpen(true); }
    }).catch(() => {});
  };

  const handleSaveEvent = async (data: ScheduleEventData) => {
    try {
      if (data.id) {
        await axios.patch(`/schedules/${id}/events/${data.id}`, data);
      } else {
        await axios.post(`/schedules/${id}/events`, data);
      }
      setModalOpen(false);
      setHasUnsaved(true);
      fetchOccurrences();
      fetchEvents();
    } catch {}
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent?.id) return;
    try {
      await axios.delete(`/schedules/${id}/events/${editingEvent.id}`);
      setModalOpen(false);
      setHasUnsaved(true);
      fetchOccurrences();
      fetchEvents();
    } catch {}
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await axios.post(`/screens/publish-schedule/${id}`);
      setHasUnsaved(false);
    } catch {}
    setPublishing(false);
  };

  const periodLabel = view === 'month'
    ? `${MONTH_NAMES[month]} ${year}`
    : view === 'week'
    ? (() => {
        const ws = getWeekStart(year, month, weekOffset);
        const we = new Date(ws); we.setDate(ws.getDate() + 6);
        return `${ws.getDate()} ${MONTH_NAMES[ws.getMonth()].slice(0,3)} – ${we.getDate()} ${MONTH_NAMES[we.getMonth()].slice(0,3)} ${we.getFullYear()}`;
      })()
    : `${DAYS_FULL[dayDate.getDay()]}, ${dayDate.getDate()} ${MONTH_NAMES[dayDate.getMonth()]} ${dayDate.getFullYear()}`;

    // ── Overlap layout ────────────────────────────────────────────────────────
  // Packs occurrences of one day into columns so nothing covers anything else.
  // clusterId groups events that transitively overlap in time; every event in a
  // cluster gets the same totalCols, so columns line up across the whole cluster.
  type Packed = { occ: OccurrenceDto; col: number; totalCols: number; clusterId: number; startMs: number; endMs: number };
  const packOverlaps = (occs: OccurrenceDto[], fromMs: number, toMs: number): Packed[] => {
    const bounds = (o: OccurrenceDto) => ({
      s: Math.max(new Date(o.startDatetime).getTime(), fromMs),
      e: Math.min(new Date(o.endDatetime).getTime(), toMs),
    });
    const sorted = [...occs].sort((a, b) => bounds(a).s - bounds(b).s);

    const colEnds: number[] = [];
    const placed = sorted.map(occ => {
      const { s, e } = bounds(occ);
      let col = colEnds.findIndex(end => end <= s);
      if (col === -1) { col = colEnds.length; colEnds.push(e); }
      else colEnds[col] = e;
      return { occ, col, startMs: s, endMs: e };
    });

    // Sorted by start, so a running cluster end is enough to find the groups
    let clusterId = -1;
    let clusterEnd = -Infinity;
    const ids = placed.map(p => {
      if (p.startMs >= clusterEnd) { clusterId++; clusterEnd = p.endMs; }
      else clusterEnd = Math.max(clusterEnd, p.endMs);
      return clusterId;
    });
    const maxCol = new Map<number, number>();
    placed.forEach((p, i) => maxCol.set(ids[i], Math.max(maxCol.get(ids[i]) ?? 0, p.col)));

    return placed.map((p, i) => ({ ...p, clusterId: ids[i], totalCols: (maxCol.get(ids[i]) ?? 0) + 1 }));
  };

  /** Hidden-event count per cluster once only `maxCols` columns are drawn. */
  const hiddenPerCluster = (packed: Packed[], maxCols: number): { clusterId: number; count: number; topMs: number }[] => {
    const out: { clusterId: number; count: number; topMs: number }[] = [];
    packed.forEach(p => {
      if (p.col < maxCols) return;
      const row = out.find(r => r.clusterId === p.clusterId);
      if (row) { row.count++; return; }
      const topMs = packed.filter(x => x.clusterId === p.clusterId).reduce((mn, x) => Math.min(mn, x.startMs), Infinity);
      out.push({ clusterId: p.clusterId, count: 1, topMs });
    });
    return out;
  };

// ── Month calendar render ──────────────────────────────────────────────────
  const renderMonth = () => {
    const MONTH_VISIBLE = 3;          // events drawn per day before the "+N" chip
    const startDow = getMonthStart(year, month);
    const daysInMonth = getDaysInMonth(year, month);
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

    const cells: React.ReactNode[] = [];

    // Leading cells (prev month)
    const prevDim = getDaysInMonth(year, month - 1 < 0 ? 11 : month - 1);
    for (let i = 0; i < startDow; i++) {
      const d = prevDim - startDow + 1 + i;
      cells.push(
        <Box key={`prev-${i}`} sx={{ border: '0 solid var(--divider)', borderRightWidth: '1px', borderBottomWidth: '1px', padding: '6px 8px', minHeight: 90, background: 'var(--glass-bg)', opacity: .5 }}>
          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mb: '4px' }}>{d}</Typography>
        </Box>
      );
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate  = new Date(year, month, d);
      const cellStart = new Date(year, month, d, 0, 0, 0);
      const cellEnd   = new Date(year, month, d, 23, 59, 59, 999);
      const isToday   = isCurrentMonth && d === today.getDate();
      // Include any occurrence that overlaps this day (not just ones that start on it)
      const dayOccs = occurrences.filter(o =>
        new Date(o.startDatetime) < cellEnd && new Date(o.endDatetime) > cellStart
      );
      // "+N" expands the whole week row: the row grows to fit its fullest day
      const rowIdx = Math.floor(cells.length / 7);
      const rowExpanded = expandedMonthRow === rowIdx;
      const shownOccs = rowExpanded ? dayOccs : dayOccs.slice(0, MONTH_VISIBLE);

      cells.push(
        <Box
          key={d}
          onClick={() => openNewEvent(cellDate)}
          sx={{
            border: '0 solid var(--divider)', borderRightWidth: '1px', borderBottomWidth: '1px',
            padding: '6px 8px', minHeight: 90, cursor: 'pointer', overflow: rowExpanded ? 'visible' : 'hidden',
            transition: 'background .1s', position: 'relative',
            '&:hover': { background: 'var(--glass-bg)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: '4px' }}>
            <Typography sx={{
              fontSize: 11, lineHeight: '22px',
              ...(isToday
                ? { background: 'var(--accent)', color: '#fff', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }
                : { color: 'var(--text-secondary)', fontWeight: 500 }),
            }}>{d}</Typography>
          </Box>
          {shownOccs.map((o, i) => {
            const occStart  = new Date(o.startDatetime);
            const occEnd    = new Date(o.endDatetime);
            const startsHere = isSameDay(occStart, cellDate);
            const endsHere   = isSameDay(occEnd, cellDate);
            const multiDay   = !startsHere || !endsHere;
            return (
              <Box
                key={i}
                onClick={e => { e.stopPropagation(); openEditEvent(o.eventId); }}
                sx={{
                  borderRadius: startsHere && endsHere ? '4px' : `${startsHere ? 4 : 0}px ${endsHere ? 4 : 0}px ${endsHere ? 4 : 0}px ${startsHere ? 4 : 0}px`,
                  padding: '2px 6px', fontSize: 10, fontWeight: 600, color: '#fff', mb: '2px',
                  background: o.eventType === 'screen_off' ? '#475569' : o.color,
                  display: 'flex', alignItems: 'center', gap: '3px',
                  // Bleed to edges for continuation segments
                  mx: multiDay ? (startsHere ? '0px' : '-8px') : '0px',
                  mr: multiDay ? (endsHere ? '0px' : '-8px') : '0px',
                  pl: startsHere ? '6px' : '4px',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  cursor: 'pointer', transition: 'opacity .12s', '&:hover': { opacity: .8 },
                }}
              >
                {!startsHere && <Box component="span" sx={{ fontSize: 8, opacity: .7, flexShrink: 0, mr: '2px' }}>◀</Box>}
                <Box component="span" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.name}</Box>
                {!endsHere && <Box component="span" sx={{ fontSize: 8, opacity: .7, flexShrink: 0, ml: '2px' }}>▶</Box>}
                {endsHere && o.repeatType !== 'none' && <Box component="span" sx={{ fontSize: 9, opacity: .7, flexShrink: 0 }}>↻</Box>}
              </Box>
            );
          })}
          {dayOccs.length > MONTH_VISIBLE && (
            <Box component="button"
              onClick={e => { e.stopPropagation(); setExpandedMonthRow(rowExpanded ? null : rowIdx); }}
              sx={{ mt: '1px', px: '4px', py: '1px', borderRadius: '4px', border: 'none', background: 'transparent', fontFamily: 'inherit',
                fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)', cursor: 'pointer', transition: 'all .12s',
                '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
              {rowExpanded ? `− ${t('common.collapse')}` : `+${dayOccs.length - MONTH_VISIBLE} ${t('common.more')}`}
            </Box>
          )}
        </Box>
      );
    }

    // Trailing cells
    const total = startDow + daysInMonth;
    const trailing = total % 7 === 0 ? 0 : 7 - (total % 7);
    for (let i = 1; i <= trailing; i++) {
      cells.push(
        <Box key={`next-${i}`} sx={{ border: '0 solid var(--divider)', borderRightWidth: '1px', borderBottomWidth: '1px', padding: '6px 8px', minHeight: 90, background: 'var(--glass-bg)', opacity: .5 }}>
          <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mb: '4px' }}>{i}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}>
        {/* Day headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
          {DAYS_SHORT.map((d, i) => (
            <Typography key={d} sx={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: i >= 5 ? 'rgba(248,113,113,.5)' : 'var(--text-disabled)', textAlign: 'center' }}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{d}</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{d[0]}</Box>
            </Typography>
          ))}
        </Box>
        {/* Cells */}
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)',
          gridTemplateRows: Array.from({ length: Math.ceil(cells.length / 7) },
            (_, r) => (expandedMonthRow === r ? 'auto' : 'minmax(90px,1fr)')).join(' ') }}>
          {cells}
        </Box>
      </Box>
    );
  };

  // ── Week calendar render ───────────────────────────────────────────────────
  const renderWeek = () => {
    const ws = getWeekStart(year, month, weekOffset);
    const today = new Date();

    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(ws);
      d.setDate(ws.getDate() + i);
      return d;
    });

    const HOUR_H = 48;                // px per hour
    const TOTAL_H = 24 * HOUR_H;
    const GAP = 2;
    const MAX_COLS = 2;               // a week column is too narrow for more than two side by side

    // Separate multi-day (spans >1 calendar day) from timed (fits within one day)
    const floorDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const multiDayOccs = occurrences.filter(o => floorDay(new Date(o.endDatetime)) > floorDay(new Date(o.startDatetime)));
    const timedOccs    = occurrences.filter(o => floorDay(new Date(o.endDatetime)) <= floorDay(new Date(o.startDatetime)));

    const hasAllDay = multiDayOccs.length > 0;
    const colSx = { flex: 1, minWidth: 0, borderRight: '1px solid var(--divider)' };

    return (
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ minWidth: 600 }}>

          {/* Header row */}
          <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 3, background: 'var(--topbar-bg)', borderBottom: '1px solid var(--divider)' }}>
            <Box sx={{ width: 48, flexShrink: 0, borderRight: '1px solid var(--divider)' }} />
            {weekDays.map((d, i) => {
              const isToday = isSameDayInTz(d, today, tz);
              const isWeekend = i >= 5;
              return (
                <Box key={i} sx={{ ...colSx, padding: '8px 4px', textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: isWeekend ? 'rgba(248,113,113,.5)' : 'var(--text-disabled)' }}>{DAYS_SHORT[i]}</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1, color: isToday ? 'var(--accent)' : 'var(--text-primary)' }}>{d.getDate()}</Typography>
                </Box>
              );
            })}
          </Box>

          {/* All-day row for multi-day events */}
          {hasAllDay && (
            <Box sx={{ display: 'flex', position: 'sticky', top: 72, zIndex: 2, background: 'var(--topbar-bg)', borderBottom: '1px solid var(--divider)' }}>
              <Box sx={{ width: 48, flexShrink: 0, borderRight: '1px solid var(--divider)', fontSize: 8, color: 'var(--text-disabled)', padding: '4px 2px', fontFamily: '"JetBrains Mono",monospace', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                all
              </Box>
              {weekDays.map((d, di) => {
                const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
                const dEnd   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
                const dayMulti = multiDayOccs.filter(o =>
                  new Date(o.startDatetime) <= dEnd && new Date(o.endDatetime) > dStart
                );
                return (
                  <Box key={di} sx={{ ...colSx, minHeight: 28, padding: '2px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {dayMulti.map((o, oi) => {
                      const startsHere = isSameDay(new Date(o.startDatetime), d);
                      const endsHere   = isSameDay(new Date(o.endDatetime), d);
                      return (
                        <Box key={oi}
                          onClick={e => { e.stopPropagation(); openEditEvent(o.eventId); }}
                          sx={{
                            borderRadius: `${startsHere ? 4 : 0}px ${endsHere ? 4 : 0}px ${endsHere ? 4 : 0}px ${startsHere ? 4 : 0}px`,
                            px: '5px', py: '1px', fontSize: 9, fontWeight: 600, color: '#fff', flexShrink: 0,
                            mx: `${startsHere ? 2 : 0}px`, mr: `${endsHere ? 2 : 0}px`,
                            background: o.eventType === 'screen_off' ? '#475569' : o.color,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            cursor: 'pointer', transition: 'opacity .12s', '&:hover': { opacity: .8 },
                          }}>
                          {startsHere ? o.name : ''}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Time area: one absolute layer per day, so events that cross an hour
              boundary still get laid out side by side instead of covering each other */}
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ width: 48, flexShrink: 0, position: 'relative', height: TOTAL_H, borderRight: '1px solid var(--divider)' }}>
              {HOURS.map(h => (
                <Box key={h} sx={{ position: 'absolute', top: h * HOUR_H, height: HOUR_H, width: '100%', boxSizing: 'border-box', borderBottom: '1px dashed var(--divider)', fontSize: 9, fontFamily: '"JetBrains Mono",monospace', color: 'var(--text-disabled)', padding: '3px 4px' }}>
                  {pad(h)}:00
                </Box>
              ))}
            </Box>

            {weekDays.map((d, di) => {
              const dayStart   = midnightInTz(d, tz);
              const dayStartMs = dayStart.getTime();
              const dayEndMs   = dayStartMs + 24 * 3_600_000;
              const dayTimed   = timedOccs.filter(o => isSameDayInTz(new Date(o.startDatetime), d, tz));
              const packed     = packOverlaps(dayTimed, dayStartMs, dayEndMs);
              const hidden     = hiddenPerCluster(packed, MAX_COLS);
              const isTodayCol = isSameDayInTz(d, today, tz);

              return (
                <Box key={di} sx={{ ...colSx, position: 'relative', height: TOTAL_H }}>
                  {/* Hour slots — click to create */}
                  {HOURS.map(h => (
                    <Box key={h} onClick={() => openNewEvent(new Date(dayStartMs + h * 3_600_000))}
                      sx={{ position: 'absolute', top: h * HOUR_H, height: HOUR_H, left: 0, right: 0, borderBottom: '1px dashed var(--divider)', cursor: 'pointer', transition: 'background .1s', '&:hover': { background: 'var(--glass-bg)' } }} />
                  ))}

                  {/* Events, capped at MAX_COLS columns */}
                  {packed.filter(p => p.col < MAX_COLS).map(({ occ, col, totalCols, startMs, endMs }) => {
                    const visCols  = Math.min(totalCols, MAX_COLS);
                    const topPx    = ((startMs - dayStartMs) / 3_600_000) * HOUR_H;
                    const heightPx = Math.max(((endMs - startMs) / 3_600_000) * HOUR_H - 2, 18);
                    return (
                      <Box key={`${occ.eventId}-${occ.startDatetime}`}
                        onClick={e => { e.stopPropagation(); openEditEvent(occ.eventId); }}
                        sx={{
                          position: 'absolute', top: topPx + 1, height: heightPx,
                          left: `calc(${GAP}px + ${col} * (100% - ${(visCols + 1) * GAP}px) / ${visCols} + ${col * GAP}px)`,
                          width: `calc((100% - ${(visCols + 1) * GAP}px) / ${visCols})`,
                          borderRadius: '5px', padding: '3px 5px', fontSize: 10, fontWeight: 600, color: '#fff',
                          overflow: 'hidden', cursor: 'pointer', zIndex: 2, boxSizing: 'border-box',
                          transition: 'opacity .12s', background: occ.eventType === 'screen_off' ? '#475569' : occ.color,
                          '&:hover': { opacity: .82 },
                        }}>
                        <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}>{occ.name}</Box>
                        {heightPx > 26 && (
                          <Box sx={{ fontSize: 8, opacity: .7, fontFamily: '"JetBrains Mono",monospace' }}>{fmtTime(occ.startDatetime)} – {fmtTime(occ.endDatetime)}</Box>
                        )}
                      </Box>
                    );
                  })}

                  {/* "+N" per overlap cluster — opens the day view, where there is room for all of them */}
                  {hidden.map(({ clusterId, count, topMs }) => (
                    <Box key={`more-${clusterId}`}
                      title={`${count} ${t('common.more')}`}
                      onClick={e => { e.stopPropagation(); setDayDate(new Date(d.getFullYear(), d.getMonth(), d.getDate())); setView('day'); }}
                      sx={{
                        position: 'absolute', top: ((topMs - dayStartMs) / 3_600_000) * HOUR_H + 2, right: 2, zIndex: 4,
                        px: '5px', py: '1px', borderRadius: '4px', fontSize: 9, fontWeight: 700, cursor: 'pointer',
                        background: 'var(--glass-bg)', border: '1px solid var(--glass-border-strong)', color: 'var(--text-primary)',
                        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                        '&:hover': { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
                      }}>
                      +{count}
                    </Box>
                  ))}

                  {/* Now-line at the current minute */}
                  {isTodayCol && (
                    <Box sx={{ position: 'absolute', top: (nowMinutes / 60) * HOUR_H, left: 0, right: 0, zIndex: 5, pointerEvents: 'none' }}>
                      <Box sx={{ position: 'absolute', left: -3, top: -3, width: 7, height: 7, borderRadius: '50%', bgcolor: 'var(--accent)' }} />
                      <Box sx={{ height: '2px', bgcolor: 'var(--accent)', opacity: 0.85 }} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  };

  // ── Day calendar render ────────────────────────────────────────────────────
  const renderDay = () => {
    const isToday  = isSameDayInTz(dayDate, new Date(), tz);
    const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
    const HOUR_H   = 56;
    const TOTAL_H  = 24 * HOUR_H;

    const dayStart = midnightInTz(dayDate, tz);
    const dayEnd_  = new Date(dayStart.getTime() + 24 * 3_600_000 - 1);

    // Include any occurrence that overlaps this day (not just ones that start here)
    const dayOccs = occurrences.filter(o =>
      new Date(o.startDatetime) < dayEnd_ && new Date(o.endDatetime) > dayStart
    );
    const sorted  = [...dayOccs].sort((a, b) =>
      Math.max(new Date(a.startDatetime).getTime(), dayStart.getTime()) -
      Math.max(new Date(b.startDatetime).getTime(), dayStart.getTime())
    );

    const MAX_COLS = 4;              // below ~4 columns a day cell is still readable
    const packed  = packOverlaps(sorted, dayStart.getTime(), dayEnd_.getTime());
    const hidden  = hiddenPerCluster(packed, MAX_COLS);
    const withCols = packed.filter(p => p.col < MAX_COLS);

    return (
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '48px 1fr', minHeight: '100%' }}>
          {/* Sticky header */}
          <Box sx={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--topbar-bg)', borderBottom: '1px solid var(--divider)', borderRight: '1px solid var(--divider)' }} />
          <Box sx={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--topbar-bg)', borderBottom: '1px solid var(--divider)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: isToday ? 'var(--accent)' : 'var(--text-primary)', lineHeight: 1 }}>{dayDate.getDate()}</Typography>
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: isWeekend ? 'rgba(248,113,113,.5)' : 'var(--text-disabled)' }}>
                {DAYS_FULL[dayDate.getDay()]}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                {MONTH_NAMES[dayDate.getMonth()]} {dayDate.getFullYear()}
              </Typography>
            </Box>
          </Box>

          {/* Time labels */}
          <Box sx={{ position: 'relative', height: TOTAL_H, borderRight: '1px solid var(--divider)' }}>
            {HOURS.map(h => (
              <Box key={h} sx={{ position: 'absolute', top: h * HOUR_H, height: HOUR_H, width: '100%', borderBottom: '1px dashed var(--divider)', fontSize: 9, fontFamily: '"JetBrains Mono",monospace', color: 'var(--text-disabled)', padding: '3px 4px', boxSizing: 'border-box' }}>
                {pad(h)}:00
              </Box>
            ))}
          </Box>

          {/* Events area */}
          <Box sx={{ position: 'relative', height: TOTAL_H }}>
            {/* Hour click slots + half-hour dividers */}
            {HOURS.map(h => {
              const cellStart = new Date(dayStart.getTime() + h * 3_600_000);
              return (
                <Box key={h} onClick={() => openNewEvent(cellStart)}
                  sx={{ position: 'absolute', top: h * HOUR_H, height: HOUR_H, left: 0, right: 0, borderBottom: '1px dashed var(--divider)', cursor: 'pointer', transition: 'background .1s', '&:hover': { background: 'var(--glass-bg)' } }}>
                  <Box sx={{ position: 'absolute', top: HOUR_H / 2, left: 0, right: 0, borderTop: '1px dashed var(--divider)', opacity: 0.4, pointerEvents: 'none' }} />
                </Box>
              );
            })}

            {/* Positioned events with column splitting */}
            {withCols.map(({ occ, col, totalCols, startMs, endMs }) => {
              const start    = new Date(occ.startDatetime);
              const end      = new Date(occ.endDatetime);
              const visCols        = Math.min(totalCols, MAX_COLS);
              const durMs          = Math.max(endMs - startMs, 15 * 60_000);
              const topPx          = ((startMs - dayStart.getTime()) / 3_600_000) * HOUR_H;
              const heightPx       = Math.max((durMs / 3_600_000) * HOUR_H - 2, 20);
              const startsHere     = isSameDay(start, dayDate);
              const endsHere       = isSameDay(end, dayDate);
              const GAP = 3;
              return (
                <Box key={`${occ.eventId}-${occ.startDatetime}`}
                  onClick={e => { e.stopPropagation(); openEditEvent(occ.eventId); }}
                  sx={{
                    position: 'absolute',
                    top: topPx + 1,
                    height: heightPx,
                    left: `calc(${GAP}px + ${col} * (100% - ${(visCols + 1) * GAP}px) / ${visCols} + ${col * GAP}px)`,
                    width: `calc((100% - ${(visCols + 1) * GAP}px) / ${visCols})`,
                    borderRadius: `${startsHere ? 6 : 0}px ${startsHere ? 6 : 0}px ${endsHere ? 6 : 0}px ${endsHere ? 6 : 0}px`,
                    padding: '5px 8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    zIndex: 2,
                    transition: 'opacity .12s',
                    background: occ.eventType === 'screen_off' ? '#475569' : occ.color,
                    color: '#fff',
                    boxSizing: 'border-box',
                    '&:hover': { opacity: .82 },
                  }}>
                  <Box sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{occ.name}</Box>
                  {heightPx > 28 && startsHere && (
                    <Box sx={{ fontSize: 10, opacity: .75, fontFamily: '"JetBrains Mono",monospace', mt: '1px' }}>
                      {fmtTime(occ.startDatetime)}{endsHere ? `–${fmtTime(occ.endDatetime)}` : ' →'}
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* "+N" per overlap cluster — expands into a full list right where it sits */}
            {hidden.map(({ clusterId, count, topMs }) => {
              const isOpen = expandedDayCluster === clusterId;
              const cluster = packed.filter(p => p.clusterId === clusterId);
              const topPx = ((topMs - dayStart.getTime()) / 3_600_000) * HOUR_H;
              return (
                <React.Fragment key={`more-${clusterId}`}>
                  <Box
                    onClick={e => { e.stopPropagation(); setExpandedDayCluster(isOpen ? null : clusterId); }}
                    sx={{ position: 'absolute', top: topPx + 2, right: 4, zIndex: 7, px: '7px', py: '2px', borderRadius: '5px',
                      fontSize: 10, fontWeight: 700, cursor: 'pointer', background: isOpen ? 'var(--accent)' : 'var(--glass-bg)',
                      border: `1px solid ${isOpen ? 'var(--accent)' : 'var(--glass-border-strong)'}`, color: isOpen ? '#fff' : 'var(--text-primary)',
                      '&:hover': { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' } }}>
                    {isOpen ? `− ${t('common.collapse')}` : `+${count} ${t('common.more')}`}
                  </Box>
                  {isOpen && (
                    <Box onClick={e => e.stopPropagation()}
                      sx={{ position: 'absolute', top: topPx + 26, left: 3, right: 3, zIndex: 6, maxHeight: 340, overflowY: 'auto',
                        borderRadius: '10px', border: '1px solid var(--glass-border-strong)', background: 'var(--glass-bg)',
                        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: '0 16px 40px rgba(0,0,0,.45)', p: '8px',
                        display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {cluster.map(({ occ }) => (
                        <Box key={`${occ.eventId}-${occ.startDatetime}`}
                          onClick={e => { e.stopPropagation(); openEditEvent(occ.eventId); }}
                          sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px', px: '8px', py: '5px', borderRadius: '6px',
                            cursor: 'pointer', background: 'var(--glass-hover)', borderLeft: `3px solid ${occ.eventType === 'screen_off' ? '#475569' : occ.color}`,
                            '&:hover': { background: 'var(--row-hover)' } }}>
                          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {occ.name}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: 'var(--text-secondary)', fontFamily: '"JetBrains Mono",monospace', flexShrink: 0 }}>
                            {fmtTime(occ.startDatetime)}–{fmtTime(occ.endDatetime)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}

            {/* Now-line: red horizontal indicator at current time */}
            {isToday && (() => {
              const topPx = (nowMinutes / 60) * HOUR_H;
              return (
                <Box sx={{ position: 'absolute', top: topPx, left: 0, right: 0, zIndex: 4, pointerEvents: 'none' }}>
                  <Box sx={{ position: 'absolute', left: -5, top: -4, width: 9, height: 9, borderRadius: '50%', bgcolor: 'var(--accent)' }} />
                  <Box sx={{ height: '2px', bgcolor: 'var(--accent)', opacity: 0.85 }} />
                </Box>
              );
            })()}
          </Box>
        </Box>
      </Box>
    );
  };

  const existingSlots: ExistingSlot[] = occurrences.map(o => ({
    eventId: o.eventId,
    startDatetime: o.startDatetime,
    endDatetime: o.endDatetime,
    name: o.name,
  }));

  const fmtCardDate = (iso: string) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, day: 'numeric', month: 'short',
    }).formatToParts(new Date(iso));
    const day = parts.find(p => p.type === 'day')?.value ?? '';
    const mon = parts.find(p => p.type === 'month')?.value ?? '';
    return `${parseInt(day)} ${mon.charAt(0).toUpperCase() + mon.slice(1, 3)}`;
  };

  const sortedEvents = [...events].sort((a, b) =>
    new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime()
  );

  /** "Every 2 days at 10:00" — the rule itself, instead of one card per occurrence. */
  const describeRepeat = (ev: ScheduleEvent): string => {
    const every = ev.repeatEvery ?? 1;
    const base = ev.repeatType === 'custom'
      ? t(
          ev.repeatUnit === 'week'  ? 'schedules.repeatEveryWeeks'
          : ev.repeatUnit === 'month' ? 'schedules.repeatEveryMonths'
          : ev.repeatUnit === 'year'  ? 'schedules.repeatEveryYears'
          : 'schedules.repeatEveryDays',
          { count: every },
        )
      : t({
          daily:    'schedules.repeatDaily',
          weekday:  'schedules.repeatWeekday',
          weekly:   'schedules.repeatWeekly',
          monthly:  'schedules.repeatMonthly',
          annually: 'schedules.repeatAnnually',
        }[ev.repeatType] ?? 'schedules.repeatDaily');

    return `${base} ${t('schedules.repeatAt', { time: fmtTime(ev.startDatetime) })}`;
  };

  const describeEnd = (ev: ScheduleEvent): string | null => {
    if (ev.repeatUntilType === 'date' && ev.repeatUntilDate) {
      return t('schedules.repeatUntil', { date: fmtCardDate(ev.repeatUntilDate) });
    }
    if (ev.repeatUntilType === 'occurrences' && ev.repeatOccurrences) {
      return t('schedules.repeatTimes', { count: ev.repeatOccurrences });
    }
    return null;
  };

  /** First occurrence of this event inside the period currently shown. */
  const nextRunOf = (eventId: number): string | null => {
    const upcoming = occurrences
      .filter(o => o.eventId === eventId)
      .sort((a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime());
    return upcoming.length ? fmtCardDate(upcoming[0].startDatetime) : null;
  };

  return (
    <Box sx={{ flex: 1, overflow: 'hidden', p: '16px', display: 'flex', flexDirection: 'column' }}>

      {/* ── Name header — mobile only: on desktop the topbar already shows back + name ── */}
      <Box sx={{ flexShrink: 0, mb: '12px', display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: '8px', px: '2px' }}>
        {/* Back + name are already in the topbar on mobile — desktop only */}
        <Box component="button" onClick={() => navigate('/schedules')}
          sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: '5px', px: '8px', py: '4px', borderRadius: '7px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0, '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
          <IcoBack /> Schedules
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', height: 14, background: 'var(--divider)', flexShrink: 0 }} />
        <Box component="input" value={name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} onBlur={handleNameBlur}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
          placeholder="Schedule name…"
          sx={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'inherit', borderBottom: '1px solid transparent', transition: 'border-color .12s', px: '2px', '&:focus': { borderBottomColor: 'var(--accent)' }, '&::placeholder': { color: 'var(--text-disabled)', fontWeight: 400 } }} />
        {savingName && <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', flexShrink: 0 }}>saving…</Typography>}

        {/* Mobile: view switch + new event live here, not in the calendar toolbar */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, border: '1px solid var(--glass-border)', borderRadius: '7px', overflow: 'hidden', flexShrink: 0 }}>
          {(['day','week','month'] as const).map(v => (
            <Box key={v} component="button" onClick={() => setView(v)} sx={{ ...vtBtnSx(view === v), px: '7px', py: '5px', fontSize: 11 }}>{t(`schedules.${v}`)}</Box>
          ))}
        </Box>
        <Box component="button" onClick={() => openNewEvent()} title={t('schedules.newEvent')}
          sx={{ display: { xs: 'inline-flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '8px', background: 'var(--accent)', border: 'none', color: '#fff', cursor: 'pointer', flexShrink: 0, '&:hover': { opacity: .88 } }}>
          <IcoPlus />
        </Box>
      </Box>

    <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, overflow: 'hidden', minHeight: 0 }}>

      {/* ── Calendar card ──────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)', borderRadius: '14px' }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '10px 16px', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
          <Box component="button" onClick={goToday} sx={{ ...navBtnSx, width: 'auto', px: 1.5, fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
            {t('schedules.today')}
          </Box>
          <Box sx={navBtnSx} onClick={prevPeriod}><IcoPrev /></Box>
          <Box sx={navBtnSx} onClick={nextPeriod}><IcoNext /></Box>
          <Typography sx={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 700, letterSpacing: '.01em' }}>{periodLabel}</Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, border: '1px solid var(--glass-border)', borderRadius: '7px', overflow: 'hidden' }}>
            <Box component="button" onClick={() => setView('day')}   sx={{ ...vtBtnSx(view === 'day'),  display: { xs: 'none', sm: 'block' } }}>{t('schedules.day')}</Box>
            <Box component="button" onClick={() => setView('week')}  sx={{ ...vtBtnSx(view === 'week'), display: { xs: 'none', sm: 'block' } }}>{t('schedules.week')}</Box>
            <Box component="button" onClick={() => setView('month')} sx={vtBtnSx(view === 'month')}>{t('schedules.month')}</Box>
          </Box>
          <Box component="button" onClick={() => openNewEvent()}
            sx={{ ...btnPrimarySx, display: { xs: 'none', md: 'inline-flex' } }}>
            <IcoPlus />{t('schedules.newEvent')}
          </Box>
        </Box>

        {/* Calendar body */}
        {view === 'month' ? renderMonth() : view === 'week' ? renderWeek() : renderDay()}
      </Box>

      {/* ── Right panel: event list ─────────────────────────────────────────── */}
      <Box sx={{ width: 310, flexShrink: 0, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)', borderRadius: '14px', overflow: 'hidden' }}>
        {/* Panel header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', flexShrink: 0 }}>
              {t('schedules.records')}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono",monospace' }}>
              {sortedEvents.length}
            </Typography>
          </Box>
          <Box component="button" onClick={handlePublish}
            disabled={publishing || (!hasUnsaved && events.length === 0)}
            title={events.length === 0 ? t('schedules.saveNeedsRecord') : ''}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '5px', borderRadius: '7px', border: 'none', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: !publishing && (hasUnsaved || events.length > 0) ? 'pointer' : 'default', flexShrink: 0, transition: 'all .15s', ...(hasUnsaved ? { background: 'linear-gradient(135deg, var(--accent), var(--accent-light))', color: '#fff', boxShadow: '0 2px 8px var(--accent-glow)' } : events.length > 0 ? { background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)' } : { background: 'var(--glass-hover)', color: 'var(--text-disabled)', opacity: 0.6 }) }}>
            {publishing ? '…' : 'Save'}
          </Box>
        </Box>

        {/* Event cards */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: '10px', display: 'flex', flexDirection: 'column', gap: '8px', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'var(--glass-border)', borderRadius: 2 } }}>
          {sortedEvents.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', textAlign: 'center', px: 2 }}>
                {t('schedules.noRecords')}
              </Typography>
            </Box>
          ) : sortedEvents.map(ev => {
            const repeating = ev.repeatType !== 'none';
            const endText = repeating ? describeEnd(ev) : null;
            const nextRun = repeating ? nextRunOf(ev.id) : null;

            return (
              <Box
                key={ev.id}
                onClick={() => openEditEvent(ev.id)}
                // flexShrink:0 — without it the flex column squeezes cards below their
                // content height, so text spills out and the list never scrolls
                sx={{ flexShrink: 0, borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'var(--glass-hover)', cursor: 'pointer', overflow: 'hidden', transition: 'all .12s', borderLeft: `3px solid ${ev.eventType === 'screen_off' ? '#475569' : ev.color}`, '&:hover': { background: 'var(--row-hover)', borderColor: 'var(--glass-border-strong)' } }}
              >
                <Box sx={{ p: '10px 11px' }}>
                  {/* Name row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
                    <Box component="span" sx={{ fontSize: 13, flexShrink: 0 }}>
                      {ev.eventType === 'screen_off' ? '⬛' : '📺'}
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.name}
                    </Typography>
                  </Box>

                  {/* Time span with start/end dates */}
                  <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: '"JetBrains Mono",monospace' }}>
                    {!repeating ? (
                      `${fmtCardDate(ev.startDatetime)} ${fmtTime(ev.startDatetime)} – ${fmtCardDate(ev.endDatetime)} ${fmtTime(ev.endDatetime)}`
                    ) : (
                      `${fmtTime(ev.startDatetime)}–${fmtTime(ev.endDatetime)}`
                    )}
                  </Typography>

                  {/* Recurrence stated once, instead of a card per occurrence */}
                  {repeating && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '5px', mt: '6px' }}>
                      <Box component="span" sx={{ fontSize: 11, color: 'var(--accent-light)', flexShrink: 0, lineHeight: 1.45 }}>↻</Box>
                      <Box>
                        <Typography sx={{ fontSize: 11, color: 'var(--text-primary)', lineHeight: 1.45 }}>
                          {describeRepeat(ev)}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', lineHeight: 1.45 }}>
                          {[t('schedules.startsOn', { date: fmtCardDate(ev.startDatetime) }), endText, nextRun && t('schedules.nextRun', { date: nextRun })]
                            .filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {ev.eventType === 'content' && ev.playlistId && (
                    <Box sx={{ display: 'flex', gap: '5px', mt: '7px', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: 10, color: 'var(--text-disabled)', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: '4px', px: '5px', py: '2px' }}>
                        🎬 {playlists.find(p => p.id === ev.playlistId)?.name ?? `#${ev.playlistId}`}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Event Modal */}
      <EventModal
        open={modalOpen}
        event={editingEvent}
        playlists={playlists}
        mediaFiles={mediaFiles}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveEvent}
        onDelete={editingEvent?.id ? handleDeleteEvent : undefined}
        lockedDate={lockedDate}
        existingSlots={existingSlots}
        companyTz={tz}
      />
    </Box>
    </Box>
  );
};

export default ScheduleEditorPage;
